import { FC } from 'react';
import { Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { Swap, SwapStatus } from '../ApiTypes';
import { formatDate } from '../utils';
import SourceView from './SourceView';

export type SwapItemLayout = 'card' | 'row';

const ROW_THUMBNAIL_WIDTH = 140;

const StyledLink = styled(Link)`
  color: inherit;
  display: block;
  border-radius: 12px;

  &:focus-visible {
    outline: 2px solid rgb(254, 167, 0);
    outline-offset: 2px;
  }
`;

const SwapItemContainer = styled.div<{ $layout: SwapItemLayout }>`
  background-color: #262626;
  border: 1px solid #333333;
  border-radius: 12px;
  padding: 8px;
  color: #f0f0f0;
  display: flex;
  flex-direction: ${props => props.$layout == 'row' ? 'row' : 'column'};
  gap: ${props => props.$layout == 'row' ? '12px' : '0px'};
`;

const ClickableSwapItemContainer = styled(SwapItemContainer)`
  transition: background-color 0.15s ease, border-color 0.15s ease, transform 0.15s ease;

  &:hover {
    background-color: #2f2f2f;
    border-color: #4d4d4d;
    transform: translateY(-2px);
  }

  @media (hover: none) {
    &:hover {
      transform: none;
    }
  }
`;

const Thumbnail = styled.div<{ $layout: SwapItemLayout }>`
  position: relative;
  flex-shrink: 0;
  ${props => props.$layout == 'row' ? `width: ${ROW_THUMBNAIL_WIDTH}px;` : ''}
`;

const Body = styled.div<{ $layout: SwapItemLayout }>`
  min-width: 0;
  flex: 1;
  padding: ${props => props.$layout == 'row' ? '2px 4px 2px 0px' : '10px 4px 4px 4px'};
`;

const OriginalTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  // 2行分の高さを確保して、カードごとに日付の位置がずれないようにする。
  min-height: 2.8em;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  word-break: break-word;
`;

const SwapInfo = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: #8a8a8a;
`;

const Badge = styled.div`
  position: absolute;
  top: 6px;
  left: 6px;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px;
  border-radius: 999px;
  background-color: rgba(0, 0, 0, 0.75);
  color: rgb(254, 167, 0);
  font-size: 11px;
  font-weight: 600;
`;

const Blink = keyframes`
  50% { opacity: 0.25; }
`;

const BadgeDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: rgb(254, 167, 0);
  animation: ${Blink} 1.2s ease-in-out infinite;
`;

const getBadgeLabel = (swap: Swap) => {
  if (swap.status == SwapStatus.GENERATED) {
    return null;
  }
  return swap.status == SwapStatus.PENDING ? <Trans>Preparing</Trans> : <Trans>Generating</Trans>;
};

const SwapItem: FC<{ swap: Swap, layout?: SwapItemLayout }> = (props) => {
  const layout = props.layout ?? 'card';
  const title = props.swap.original?.detected.title;
  const badgeLabel = getBadgeLabel(props.swap);

  return <StyledLink to={`/swaps/${props.swap.swapId}`}>
    <ClickableSwapItemContainer $layout={layout}>
      <Thumbnail $layout={layout}>
        <SourceView original={props.swap.original} face={props.swap.face} swap={props.swap} />
        {badgeLabel && <Badge><BadgeDot />{badgeLabel}</Badge>}
      </Thumbnail>
      <Body $layout={layout}>
        <OriginalTitle title={title}>{title ?? <Trans>Video</Trans>}</OriginalTitle>
        <SwapInfo>{formatDate(props.swap.createdAt)}</SwapInfo>
      </Body>
    </ClickableSwapItemContainer>
  </StyledLink>;
};

const Shimmer = keyframes`
  100% { background-position-x: -200%; }
`;

const SkeletonBlock = styled.div`
  border-radius: 6px;
  background: linear-gradient(100deg, #2f2f2f 40%, #3a3a3a 50%, #2f2f2f 60%) right / 200% 100%;
  animation: ${Shimmer} 1.4s linear infinite;
`;

const SkeletonThumbnail = styled(SkeletonBlock)<{ $layout: SwapItemLayout }>`
  aspect-ratio: 16 / 9;
  border-radius: 10px;
  flex-shrink: 0;
  ${props => props.$layout == 'row' ? `width: ${ROW_THUMBNAIL_WIDTH}px;` : 'width: 100%;'}
`;

const SkeletonLine = styled(SkeletonBlock)<{ $width: string }>`
  height: 12px;
  width: ${props => props.$width};

  & + & {
    margin-top: 8px;
  }
`;

export const SwapItemSkeleton: FC<{ layout?: SwapItemLayout }> = (props) => {
  const layout = props.layout ?? 'card';

  return <SwapItemContainer $layout={layout}>
    <SkeletonThumbnail $layout={layout} />
    <Body $layout={layout}>
      <SkeletonLine $width="90%" />
      <SkeletonLine $width="55%" />
    </Body>
  </SwapItemContainer>;
};

export default SwapItem;
